'use client';

/**
 * Testimonials Section
 *
 * Displays customer reviews with elegant styling and star ratings
 */

import Image from 'next/image';
import { Testimonial } from '@/lib/mock-data';
import { Star, Quote } from 'lucide-react';

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
  title?: string;
  subtitle?: string;
}

export function TestimonialsSection({
  testimonials,
  title = 'What Our VIP Guests Say',
  subtitle = 'Discover why thousands choose Ticket to Luxury for unforgettable concert experiences'
}: TestimonialsSectionProps) {
  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, index) => (
          <Star
            key={index}
            className={`h-5 w-5 ${
              index < rating
                ? 'fill-primary text-primary'
                : 'fill-muted text-muted'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  return (
    <section className="py-20 md:py-32">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold">
            {title}
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            {subtitle}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="glass-card p-8 space-y-6 hover-glow animate-in slide-in-from-bottom-10 duration-700"
            >
              {/* Quote Icon */}
              <div className="flex justify-between items-start">
                <Quote className="h-10 w-10 text-primary/30" />
                {renderStars(testimonial.rating)}
              </div>

              {/* Review Text */}
              <p className="text-base text-foreground/90 leading-relaxed">
                "{testimonial.review}"
              </p>

              {/* Customer Info */}
              <div className="flex items-center gap-4 pt-4 border-t border-primary/20">
                <div className="relative w-14 h-14 rounded-full overflow-hidden ring-2 ring-primary/30">
                  <Image
                    src={testimonial.avatarUrl}
                    alt={testimonial.customerName}
                    fill
                    className="object-cover"
                  />
                </div>

                <div className="flex-1">
                  <h4 className="font-semibold text-foreground">
                    {testimonial.customerName}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {testimonial.location}
                  </p>
                </div>
              </div>

              {/* Event Attended */}
              <div className="pt-2">
                <p className="text-xs text-muted-foreground">Attended</p>
                <p className="text-sm text-primary font-medium">
                  {testimonial.eventAttended}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatDate(testimonial.date)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badge */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-primary/10 backdrop-blur-sm border border-primary/30">
            <Star className="h-5 w-5 fill-primary text-primary" />
            <span className="text-base font-medium text-foreground">
              Rated 5/5 stars by over 2,000 VIP guests
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
